import { useMemo, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { T, useGT } from 'gt-react';
import { gridBands } from '@/components/report-canvas/grid';
import { ReportCanvasView } from '@/components/report-canvas/ReportCanvasView';
import { ReportDataContext } from '@/components/report-canvas/ReportDataContext';
import { ComponentRegistryContext } from '@/components/report-canvas/registry-context';
import type { GridLayout } from '@/components/report-canvas/types';
import type { WorkbookReportData } from './useWorkbookReportData';
import { WorkbookReportHeading } from './WorkbookReportHeading';
import { workbookReportRegistry } from './workbookReportRegistry';
import {
  resolveReportFilters,
  useFilteredDatasets,
  WorkbookReportScope,
} from './workbookReportScope';
import type { ReportFilter } from '@erp/shared-types';
import { useReportFilterSummary } from '@/components/report-canvas/filters/filterSummary';

/**
 * The print width in CSS pixels: a US Letter landscape page's content width
 * (10 in at 96 px/in), so charts lay out as they will print.
 */
export const REPORT_PRINT_WIDTH = 960;
/** Marks each part of the print render: the header, then each grid band, in order. */
export const REPORT_PRINT_PART = 'data-report-print-part';

export type WorkbookReportPrintHeader = {
  title: string;
  description?: string;
  /** e.g. the Workbook's name and when its Results were computed. */
  subtitle: string;
};

/**
 * A Report laid out for its PDF, off screen: the header, then one part per
 * grid band (the runs of grid rows no component straddles; see `gridBands`),
 * each read-only as viewers see it. Parts are captured separately so a page
 * is only ever cut between bands, never through a component.
 */
export function WorkbookReportPrint({
  ref,
  layout,
  data,
  header,
  neverRun,
  paletteId,
  filters,
}: {
  ref: Ref<HTMLDivElement>;
  layout: GridLayout;
  data: WorkbookReportData;
  header: WorkbookReportPrintHeader;
  neverRun: boolean;
  paletteId?: string;
  /** The Report's saved filters: the PDF shows the Report as saved. */
  filters?: readonly ReportFilter[];
}) {
  const gt = useGT();
  const today = new Date().toDateString();
  const resolved = useMemo(
    () => resolveReportFilters(filters, new Date(today)),
    [filters, today],
  );
  const datasets = useFilteredDatasets(data.datasets, resolved);
  const summarize = useReportFilterSummary();
  const filterSummary = summarize(
    filters,
    new Date(today),
    (filter) =>
      filter.label?.trim() ||
      data.datasets
        .flatMap((dataset) => dataset.columns)
        .find((column) => column.key === filter.column)?.label ||
      filter.column,
  );
  const dataValue = useMemo(
    () => ({
      datasets,
      defaultDatasetKey: data.defaultDatasetKey,
      emptyHint: neverRun
        ? gt('Run the workbook to see this report.', {
            context: 'workbook report never run',
          })
        : undefined,
    }),
    [datasets, data.defaultDatasetKey, neverRun, gt],
  );
  const part = { [REPORT_PRINT_PART]: '' };
  return createPortal(
    <div
      ref={ref}
      aria-hidden
      inert
      className="fixed top-0 flex flex-col gap-2 bg-background p-4 text-foreground [&_*]:[scrollbar-width:none] [&_[data-component-title]]:whitespace-normal"
      // Off screen but laid out, so charts measure their size and render;
      // no scrollbars, which a page can't scroll; component titles wrap, as a
      // page has no hover to show a cut one.
      style={{ left: -100_000, width: REPORT_PRINT_WIDTH }}
    >
      <div {...part} className="flex flex-col gap-1 pb-1">
        <WorkbookReportHeading heading={header} />
        <p className="text-xs text-muted-foreground">{header.subtitle}</p>
        {filterSummary && (
          <p className="text-xs text-muted-foreground">{filterSummary}</p>
        )}
      </div>
      <ComponentRegistryContext.Provider value={workbookReportRegistry}>
        <ReportDataContext.Provider value={dataValue}>
          <WorkbookReportScope
            data={data}
            paletteId={paletteId}
            filters={resolved}
          >
            {gridBands(layout).map((band) => (
              <div key={band.top} {...part}>
                <ReportCanvasView layout={band.rects} />
              </div>
            ))}
          </WorkbookReportScope>
        </ReportDataContext.Provider>
      </ComponentRegistryContext.Provider>
      {!layout.length && (
        <p {...part} className="text-sm text-muted-foreground">
          <T context="workbook report empty">No charts yet.</T>
        </p>
      )}
    </div>,
    document.body,
  );
}
